import type { RowClassParams } from "ag-grid-enterprise";
import { ForecastMetaDataType } from "../../../hooks/useFetchForecastMetaData";
import { ForecastTableControlValues } from "../../tableControls/forecastTableControlValues";
import { COLOR_PALETTE } from "../ForecastDataTableConfig";

/**
 * Returns unique styling per kpi in forecast grid.
 * Applied as default and when 'kpi' radio button is selected.
 */
export function applyForecastKPIStyling(
  params: Partial<RowClassParams>,
  metaData: ForecastMetaDataType
) {
  const { metric_name: metricName } = params.data;

  // Return a default style if metricName is not found in metricSortOrder
  if (!metricName || !(metricName in metaData.metricSortOrder)) {
    return {};
  }

  const order = metaData.metricSortOrder[metricName];
  const colorIndex = order % COLOR_PALETTE.length;
  return { backgroundColor: COLOR_PALETTE[colorIndex] };
}

/**
 * Returns unique styling per kpi family in forecast grid.
 * Applied when 'kpi family' radio button is selected.
 */
export function applyForecastKPIFamilyStyling(
  params: Partial<RowClassParams>,
  metaData: ForecastMetaDataType
) {
  const { metric_name: metricName } = params.data;

  // Return a default style if metricName is not found in metricBlockMapping
  if (!metricName || !(metricName in metaData.metricBlockMapping)) {
    return {};
  }

  const parents = new Set(Object.values(metaData.metricBlockMapping));
  const parentsArray = [...parents];
  const parentIndex = parentsArray.indexOf(
    metaData.metricBlockMapping[metricName]
  );
  const colorIndex = parentIndex % COLOR_PALETTE.length;
  return { backgroundColor: COLOR_PALETTE[colorIndex] };
}

/**
 * Based on which styling mode is selected in radio buttons ('none', 'kpi', 'kpi family'),
 * the corresponding styling function is called, and its styling is returned.
 */
export function getRowStyle(
  selectedView: ForecastTableControlValues["gridStyleGrouping"],
  params: Partial<RowClassParams>,
  metaData: ForecastMetaDataType
): { backgroundColor?: string } {
  if (selectedView === "KPI") {
    return applyForecastKPIStyling(params, metaData);
  }

  if (selectedView === "KPI Family") {
    return applyForecastKPIFamilyStyling(params, metaData);
  }

  return {};
}
